import { prisma, AbilityResourceV2, AbilityActionV2 } from "client";

export const createAbility = async (params: {
  resource: AbilityResourceV2;
  action: AbilityActionV2;
  name?: string;
  description?: string | null;
}) => {
  const { resource, action, name, description } = params;
  const computedName = name ?? `${resource}:${action}`;
  return prisma.ability.create({
    data: {
      name: computedName,
      resource,
      action,
      description: description ?? undefined,
    },
  });
};

export const upsertAbility = async (params: {
  resource: AbilityResourceV2;
  action: AbilityActionV2;
  name?: string;
  description?: string | null;
}) => {
  const { resource, action, name, description } = params;
  const computedName = name ?? `${resource}:${action}`;
  const existing = await prisma.ability.findFirst({
    where: { resource, action },
    select: { id: true },
  });
  if (existing) {
    return prisma.ability.update({
      where: { id: existing.id },
      data: { name: computedName, description: description ?? undefined },
    });
  }
  return prisma.ability.create({
    data: {
      name: computedName,
      resource,
      action,
      description: description ?? undefined,
    },
  });
};

export const createRoleV2 = async (params: {
  name: string;
  description?: string | null;
  abilityNames?: string[];
  abilities?: { resource: AbilityResourceV2; action: AbilityActionV2 }[];
}) => {
  const { name, description, abilityNames, abilities: abilityPairs } = params;
  const byNames =
    abilityNames && abilityNames.length > 0
      ? await prisma.ability.findMany({
          where: { name: { in: abilityNames } },
          select: { id: true },
        })
      : [];
  const byPairs =
    abilityPairs && abilityPairs.length > 0
      ? await prisma.ability.findMany({
          where: {
            OR: abilityPairs.map((p) => ({
              resource: p.resource,
              action: p.action,
            })),
          },
          select: { id: true },
        })
      : [];

  return prisma.roleV2.create({
    data: {
      name,
      description: description ?? undefined,
      abilityIds: [...byNames, ...byPairs].map((a) => a.id),
    },
  });
};

export const upsertRoleV2ByName = async (params: {
  name: string;
  description?: string | null;
  abilityNames?: string[];
  abilities?: { resource: AbilityResourceV2; action: AbilityActionV2 }[];
}) => {
  const { name, description, abilityNames, abilities: abilityPairs } = params;
  const byNames =
    abilityNames && abilityNames.length > 0
      ? await prisma.ability.findMany({
          where: { name: { in: abilityNames } },
          select: { id: true },
        })
      : [];
  const byPairs =
    abilityPairs && abilityPairs.length > 0
      ? await prisma.ability.findMany({
          where: {
            OR: abilityPairs.map((p) => ({
              resource: p.resource,
              action: p.action,
            })),
          },
          select: { id: true },
        })
      : [];

  return prisma.roleV2.upsert({
    where: { name },
    update: {
      description: description ?? undefined,
      abilityIds: [...byNames, ...byPairs].map((a) => a.id),
    },
    create: {
      name,
      description: description ?? undefined,
      abilityIds: [...byNames, ...byPairs].map((a) => a.id),
    },
  });
};

export const assignAbilitiesToRoleV2 = async (params: {
  roleId: string;
  abilityNames?: string[];
  abilities?: { resource: AbilityResourceV2; action: AbilityActionV2 }[];
}) => {
  const { roleId, abilityNames, abilities: abilityPairs } = params;
  const byNames =
    abilityNames && abilityNames.length > 0
      ? await prisma.ability.findMany({
          where: { name: { in: abilityNames } },
          select: { id: true },
        })
      : [];
  const byPairs =
    abilityPairs && abilityPairs.length > 0
      ? await prisma.ability.findMany({
          where: {
            OR: abilityPairs.map((p) => ({
              resource: p.resource,
              action: p.action,
            })),
          },
          select: { id: true },
        })
      : [];
  return prisma.roleV2.update({
    where: { id: roleId },
    data: { abilityIds: [...byNames, ...byPairs].map((a) => a.id) },
  });
};

export const getRolesV2Paginated = async (
  pageNumber: number = 1,
  pageSize: number = 10,
  searchByName?: string
) => {
  const where = searchByName ? { name: { contains: searchByName } } : {};
  const [total, data] = await Promise.all([
    prisma.roleV2.count({ where }),
    prisma.roleV2.findMany({
      where,
      skip: (pageNumber - 1) * pageSize,
      take: pageSize,
      orderBy: { name: "asc" },
      select: { id: true, name: true, description: true, abilityIds: true },
    }),
  ]);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return {
    data,
    pagination: {
      pageNumber,
      pageSize,
      total,
      totalPages,
      hasNextPage: pageNumber < totalPages,
    },
  };
};

export const getAbilityOptionsV2 = () => {
  const resources = Object.values(AbilityResourceV2) as AbilityResourceV2[];
  const actions = Object.values(AbilityActionV2) as AbilityActionV2[];
  return resources.map((resource) => ({
    resource,
    actions: actions.map((action) => ({
      action,
      name: `${resource}:${action}`,
    })),
  }));
};

export const setUserRoleV2 = async (params: {
  userId: string;
  roleId: string | null;
}) => {
  const { userId, roleId } = params;
  return prisma.user.update({
    where: { id: userId },
    data: { roleV2Id: roleId ?? undefined },
  });
};

export const setProfileRoleV2 = async (params: {
  profileId: string;
  roleId: string | null;
}) => {
  const { profileId, roleId } = params;
  return prisma.profile.update({
    where: { id: profileId },
    data: { roleV2Id: roleId ?? undefined },
  });
};

type AssignmentInput =
  | { type: "EVENT"; id: string }
  | { type: "WHITE_LABEL"; id: string }
  | { type: "ORGANIZATION"; id: string };

export const userHasAbilityV2 = async (params: {
  userId: string;
  abilityName?: string;
  resource?: AbilityResourceV2;
  action?: AbilityActionV2;
  assignment?: AssignmentInput;
}): Promise<boolean> => {
  const { userId, abilityName, resource, action, assignment } = params;

  const ability = abilityName
    ? await prisma.ability.findUnique({
        where: { name: abilityName },
        select: { id: true },
      })
    : resource && action
    ? await prisma.ability.findFirst({
        where: { resource, action },
        select: { id: true },
      })
    : null;
  if (!ability) return false;

  const user = await prisma.user.findUnique({
    where: { id: userId },
    select: { roleV2Id: true },
  });
  if (user?.roleV2Id) {
    const userRoleHas = await prisma.roleV2.findFirst({
      where: { id: user.roleV2Id, abilityIds: { has: ability.id } },
      select: { id: true },
    });
    if (userRoleHas) return true;
  }

  if (assignment?.type === "EVENT") {
    const profile = await prisma.profile.findFirst({
      where: { userId, eventId: assignment.id },
      select: { roleV2Id: true },
    });
    if (profile?.roleV2Id) {
      const profileRoleHas = await prisma.roleV2.findFirst({
        where: { id: profile.roleV2Id, abilityIds: { has: ability.id } },
        select: { id: true },
      });
      if (profileRoleHas) return true;
    }
  }

  return false;
};

export const getUserAbilitiesV2 = async (params: {
  userId: string;
  eventId?: string;
}): Promise<
  { name: string; resource: AbilityResourceV2; action: AbilityActionV2 }[]
> => {
  const { userId, eventId } = params;

  const abilityIds = new Set<string>();

  const user = await prisma.user.findUnique({
    where: { id: userId },
    select: { roleV2Id: true },
  });
  if (user?.roleV2Id) {
    const role = await prisma.roleV2.findUnique({
      where: { id: user.roleV2Id },
      select: { abilityIds: true },
    });
    role?.abilityIds.forEach((id) => abilityIds.add(id));
  }

  if (eventId) {
    const profile = await prisma.profile.findFirst({
      where: { userId, eventId },
      select: { roleV2Id: true },
    });
    if (profile?.roleV2Id) {
      const role = await prisma.roleV2.findUnique({
        where: { id: profile.roleV2Id },
        select: { abilityIds: true },
      });
      role?.abilityIds.forEach((id) => abilityIds.add(id));
    }
  }

  if (abilityIds.size === 0) return [];

  const abilities = await prisma.ability.findMany({
    where: { id: { in: Array.from(abilityIds) } },
    select: { name: true, resource: true, action: true },
  });
  return abilities;
};

export const listAbilitiesV2 = async () => {
  return prisma.ability.findMany({
    orderBy: [{ resource: "asc" }, { action: "asc" }, { name: "asc" }],
  });
};

export const listRolesV2 = async () => {
  return prisma.roleV2.findMany({
    orderBy: [{ name: "asc" }],
    select: { id: true, name: true, description: true, abilityIds: true },
  });
};

export const ensureDefaultAbilitiesV2 = async () => {
  const resources: AbilityResourceV2[] = [
    "EVENT",
    "WHITE_LABEL",
    "ORGANIZATION",
    "DOOR_AGENT",
    "REPORTS",
    "ORDER",
  ];
  const actions: AbilityActionV2[] = ["CREATE", "READ", "UPDATE", "DELETE"];
  for (const resource of resources) {
    for (const action of actions) {
      const existing = await prisma.ability.findFirst({
        where: { resource, action },
        select: { id: true },
      });
      if (!existing) {
        await prisma.ability.create({
          data: { name: `${resource}:${action}`, resource, action },
        });
      }
    }
  }
};

export const ensureDefaultRolesV2 = async () => {
  await ensureDefaultAbilitiesV2();

  const abilityRecords = await prisma.ability.findMany({
    select: { id: true, resource: true, action: true },
  });

  const allActions: AbilityActionV2[] = ["CREATE", "READ", "UPDATE", "DELETE"];

  const pick = (
    resource: AbilityResourceV2,
    actions: AbilityActionV2[] = allActions
  ) => {
    return abilityRecords
      .filter(
        (ability) =>
          ability.resource === resource && actions.includes(ability.action)
      )
      .map((ability) => ability.id);
  };

  const mergeIds = (...lists: string[][]) =>
    Array.from(new Set(lists.flat())).filter(Boolean);

  const rolesToEnsure = [
    {
      name: "SUPER_ADMIN",
      description: "Super admin with full access to the entire system",
      abilityIds: abilityRecords.map((ability) => ability.id),
    },
    {
      name: "WHITE_LABEL_SUPER_ADMIN",
      description:
        "Super admin with full access to the assigned white label, organizations, events, and door agents",
      abilityIds: mergeIds(
        pick("WHITE_LABEL"),
        pick("ORGANIZATION"),
        pick("EVENT"),
        pick("DOOR_AGENT"),
        pick("REPORTS"),
        pick("ORDER")
      ),
    },
    {
      name: "WHITE_LABEL_ADMIN",
      description:
        "White label admin with full access except managing white label super admins",
      abilityIds: mergeIds(
        pick("WHITE_LABEL", ["CREATE", "READ", "UPDATE"]),
        pick("ORGANIZATION"),
        pick("EVENT"),
        pick("DOOR_AGENT"),
        pick("REPORTS", ["READ", "UPDATE"]),
        pick("ORDER")
      ),
    },
    {
      name: "ORGANIZATION_SUPER_ADMIN",
      description:
        "Organization super admin with full access to assigned organizations and related events",
      abilityIds: mergeIds(
        pick("ORGANIZATION"),
        pick("EVENT"),
        pick("DOOR_AGENT"),
        pick("REPORTS"),
        pick("ORDER")
      ),
    },
    {
      name: "ORGANIZATION_OWNER",
      description:
        "Organization owner with full management access except deleting the organization",
      abilityIds: mergeIds(
        pick("ORGANIZATION", ["CREATE", "READ", "UPDATE"]),
        pick("EVENT"),
        pick("DOOR_AGENT"),
        pick("REPORTS"),
        pick("ORDER")
      ),
    },
    {
      name: "ORGANIZATION_MANAGER",
      description:
        "Organization manager with permissions to manage events, organizers, and door agents",
      abilityIds: mergeIds(
        pick("ORGANIZATION", ["READ", "UPDATE"]),
        pick("EVENT", ["CREATE", "READ", "UPDATE"]),
        pick("DOOR_AGENT", ["CREATE", "READ", "UPDATE"]),
        pick("REPORTS", ["READ", "UPDATE"]),
        pick("ORDER")
      ),
    },
    {
      name: "ORGANIZATION_ADMIN",
      description:
        "Organization admin with permissions to manage specific events within the organization",
      abilityIds: mergeIds(
        pick("EVENT", ["CREATE", "READ", "UPDATE"]),
        pick("DOOR_AGENT", ["READ", "UPDATE"]),
        pick("REPORTS", ["READ"]),
        pick("ORDER")
      ),
    },
    {
      name: "ORGANIZATION_MEMBER",
      description:
        "Organization member with read access to assigned events and reports",
      abilityIds: mergeIds(
        pick("EVENT", ["READ"]),
        pick("REPORTS", ["READ"]),
        pick("ORDER")
      ),
    },
    {
      name: "DOOR_AGENT",
      description: "Door agent with access to door management and check-in",
      abilityIds: mergeIds(
        pick("DOOR_AGENT", ["READ", "UPDATE"]),
        pick("EVENT", ["READ"])
        // pick("ORDER", )
      ),
    },
  ];

  for (const role of rolesToEnsure) {
    await upsertRoleV2ByName(role);
  }
};

export const getRoleByNameV2 = async (name: string) => {
  return prisma.roleV2.findUnique({
    where: { name },
    select: { id: true, name: true, description: true, abilityIds: true },
  });
};

export const checkRoleHasAbilityV2 = async (params: {
  roleName: string;
  abilityName: string;
}) => {
  try {
    const { roleName, abilityName } = params;
    const role = await prisma.roleV2.findUnique({
      where: { name: roleName },
      select: { id: true },
    });

    if (!role) return false;

    const ability = await prisma.ability.findUnique({
      where: {
        name: abilityName,
      },
    });
    if (!ability) return false;
    const roleHasAbility = await prisma.roleV2.findFirst({
      where: {
        id: role.id,
        abilityIds: {
          has: ability.id,
        },
      },
      select: {
        id: true,
      },
    });
    return roleHasAbility ? true : false;
  } catch (error) {
    console.error("Error checking role has ability", error);
    return false;
  }
};

export const getRoleV2ById = async (id: string) => {
  return prisma.roleV2.findUnique({
    where: { id },
  });
};
